import { useRef, useState } from "react";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageUpload";
import RichTextEditorPro from "@/components/RichTextEditorPro";

/**
 * Admin editor block for the magazine-style profile layout:
 *   - 3 top-photo slots (individually replaceable)
 *   - 2 rich-text columns (left / right)
 * Used inside Institution / Supplier / EdTech editor modals.
 */
export default function MagazineLayoutFields({
  photos,        // string[] length up to 3
  onPhotosChange,
  leftHtml,
  onLeftChange,
  rightHtml,
  onRightChange,
  testId = "mag",
}) {
  const [busyIdx, setBusyIdx] = useState(-1);
  const fileRefs = [useRef(null), useRef(null), useRef(null)];
  const slots = [0, 1, 2].map((i) => (photos && photos[i]) || "");

  const replace = async (idx, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setBusyIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = [...slots];
      next[idx] = data.url;
      onPhotosChange(next.filter(Boolean).length ? next : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusyIdx(-1);
      if (fileRefs[idx].current) fileRefs[idx].current.value = "";
    }
  };

  const clear = (idx) => {
    const next = [...slots];
    next[idx] = "";
    onPhotosChange(next);
  };

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 space-y-5" data-testid={`${testId}-block`}>
      <div>
        <div className="text-[11px] font-bold tracking-[0.2em] text-amber-800 uppercase">Magazine Layout</div>
        <p className="text-xs text-slate-600 mt-1">
          When either column below has content, the public profile switches to a 2-column magazine layout with
          the 3 top photos above. Leave everything blank to keep the classic layout.
        </p>
      </div>

      {/* 3 photo slots */}
      <div>
        <label className="text-xs text-slate-600 mb-2 block flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5"/> Top 3 photos (16:9)
        </label>
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => {
            const src = slots[i];
            return (
              <div key={i} className="relative aspect-[16/9] rounded-xl overflow-hidden bg-white border border-slate-200 group">
                <input
                  ref={fileRefs[i]}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => replace(i, e.target.files?.[0])}
                  data-testid={`${testId}-photo-${i}-input`}
                />
                {src ? (
                  <>
                    <img src={resolveImageUrl(src)} alt={`slot ${i + 1}`} className="w-full h-full object-cover"/>
                    <button
                      type="button"
                      onClick={() => clear(i)}
                      className="absolute top-1.5 right-1.5 p-1 rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100"
                      data-testid={`${testId}-photo-${i}-clear`}
                    >
                      <X className="w-3.5 h-3.5"/>
                    </button>
                    <button
                      type="button"
                      onClick={() => fileRefs[i].current?.click()}
                      disabled={busyIdx === i}
                      className="absolute bottom-1.5 right-1.5 px-2.5 py-1 rounded-full bg-white/95 text-slate-800 text-[11px] font-semibold shadow-sm"
                    >
                      {busyIdx === i ? "…" : "Replace"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRefs[i].current?.click()}
                    disabled={busyIdx === i}
                    className="w-full h-full flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-sky-700 hover:bg-sky-50/60"
                    data-testid={`${testId}-photo-${i}-add`}
                  >
                    {busyIdx === i ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                    <span className="text-[11px]">{busyIdx === i ? "Uploading…" : `Add photo ${i + 1}`}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Left column editor */}
      <div>
        <label className="text-xs text-slate-600 mb-1 block">Left column (name, logo, address, intro…)</label>
        <RichTextEditorPro value={leftHtml} onChange={onLeftChange} minHeight="260px" testId={`${testId}-left`}/>
      </div>

      {/* Right column editor */}
      <div>
        <label className="text-xs text-slate-600 mb-1 block">Right column (history, schools, details…)</label>
        <RichTextEditorPro value={rightHtml} onChange={onRightChange} minHeight="260px" testId={`${testId}-right`}/>
      </div>
    </div>
  );
}
